import React, { useState } from 'react';
import {
    Form,
    Input,
    Button,
    Typography,
    message
} from 'antd';
import { LockOutlined, CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import * as adminactions from "store/actions/AuthActions";
import OTPInput from './OTPInput';

const { Title, Text } = Typography;

const PasswordReset = ({ email: prefilledEmail, startFromOTP = false, onBack, onSuccess, embedded = false }) => {
    const [currentStep, setCurrentStep] = useState(startFromOTP ? 2 : 1);
    const [email, setEmail] = useState(prefilledEmail || '');
    const [otpCode, setOtpCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const handleEmailSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await adminactions.requestPasswordReset(values.email);
            if (response && !response.message?.includes('Error')) {
                setEmail(values.email);
                setCurrentStep(2);
                message.success('Password reset OTP sent to your email!');
            } else {
                message.error(response.message || 'Failed to send reset email');
            }
        } catch (error) {
            message.error('Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    const handleOTPVerify = (response, code) => {
        if (response && response.valid) {
            setOtpCode(code);
            setCurrentStep(3);
            message.success('OTP verified! You can now set your new password.');
        } else {
            message.error(response?.message || 'OTP verification failed');
        }
    };

    const handlePasswordReset = async (values) => {
        setLoading(true);
        try {
            const response = await adminactions.confirmPasswordReset(
                email,
                otpCode,
                values.password,
                values.confirmPassword
            );
            if (response && !response.message?.includes('Error')) {
                message.success('Password reset successful!');
                onSuccess && onSuccess();
            } else {
                message.error(response.message || 'Password reset failed');
            }
        } catch (error) {
            message.error('Password reset failed');
        } finally {
            setLoading(false);
        }
    };

    const stepLabels = ['Email', 'Verify', 'New Password'];

    const renderStepIndicator = () => (
        <div className="reset-step-indicator">
            {stepLabels.map((label, i) => {
                const stepNum = i + 1;
                const isActive = currentStep === stepNum;
                const isComplete = currentStep > stepNum;
                return (
                    <div key={i} className={`reset-step ${isActive ? 'active' : ''} ${isComplete ? 'complete' : ''}`}>
                        <div className="reset-step-dot">
                            {isComplete ? <CheckCircleOutlined /> : stepNum}
                        </div>
                        <span className="reset-step-label">{label}</span>
                        {i < 2 && <div className={`reset-step-line ${isComplete ? 'complete' : ''}`} />}
                    </div>
                );
            })}
        </div>
    );

    const renderContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <>
                        <div className="reset-header">
                            <div className="reset-icon-circle">
                                <LockOutlined />
                            </div>
                            <Title level={3} style={{ margin: '0 0 8px 0', color: '#1a1a2e' }}>
                                Reset Password
                            </Title>
                            <Text style={{ color: '#64748b', fontSize: 14 }}>
                                Enter your email and we'll send you a verification code
                            </Text>
                        </div>

                        <Form form={form} onFinish={handleEmailSubmit} layout="vertical" size="large">
                            <Form.Item
                                name="email"
                                label="Email Address"
                                rules={[
                                    { required: true, message: 'Please enter your email!' },
                                    { type: 'email', message: 'Please enter a valid email!' }
                                ]}
                            >
                                <Input
                                    placeholder="you@company.com"
                                    className="login-input"
                                />
                            </Form.Item>

                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={loading}
                                className="login-submit-btn"
                            >
                                Send Verification Code
                            </Button>

                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <Button type="link" onClick={onBack} icon={<ArrowLeftOutlined />} className="login-link">
                                    Back to Login
                                </Button>
                            </div>
                        </Form>
                    </>
                );

            case 2:
                return (
                    <OTPInput
                        embedded={embedded}
                        email={email}
                        onVerify={handleOTPVerify}
                        onBack={() => setCurrentStep(1)}
                        title="Verify Reset Code"
                        subtitle="Enter the OTP sent to your email to reset password"
                        otpType="password_reset"
                    />
                );

            case 3:
                return (
                    <>
                        <div className="reset-header">
                            <div className="reset-icon-circle success">
                                <CheckCircleOutlined />
                            </div>
                            <Title level={3} style={{ margin: '0 0 8px 0', color: '#1a1a2e' }}>
                                Set New Password
                            </Title>
                            <Text style={{ color: '#64748b', fontSize: 14 }}>
                                Create a strong password for your account
                            </Text>
                        </div>

                        <Form onFinish={handlePasswordReset} layout="vertical" size="large">
                            <Form.Item
                                name="password"
                                label="New Password"
                                rules={[
                                    { required: true, message: 'Please enter your password!' },
                                    { min: 8, message: 'Password must be at least 8 characters!' }
                                ]}
                            >
                                <Input.Password
                                    placeholder="Minimum 8 characters"
                                    className="login-input"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label="Confirm Password"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Please confirm your password!' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Passwords do not match!'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    placeholder="Re-enter your password"
                                    className="login-input"
                                />
                            </Form.Item>

                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                loading={loading}
                                className="login-submit-btn"
                            >
                                Reset Password
                            </Button>

                            <div style={{ textAlign: 'center', marginTop: 16 }}>
                                <Button type="link" onClick={() => setCurrentStep(2)} className="login-link">
                                    Back to OTP
                                </Button>
                            </div>
                        </Form>
                    </>
                );

            default:
                return null;
        }
    };

    if (embedded) {
        return (
            <div className="reset-embedded">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={onBack}
                    className="login-back-btn"
                >
                    Back to Login
                </Button>
                {renderStepIndicator()}
                {renderContent()}
            </div>
        );
    }

    // Standalone mode (fallback)
    return (
        <div className="login-page">
            <div className="login-form-panel" style={{ flex: 1 }}>
                <div className="login-form-container">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBack}
                        className="login-back-btn"
                    >
                        Back to Login
                    </Button>
                    {renderStepIndicator()}
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default PasswordReset;
